import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Grid2 as Grid,
  Paper,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tab,
  Tabs,
  TabPanel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Chat,
  Send,
  Search,
  Assignment,
  Build,
  Person,
  Circle,
  AttachFile,
  MoreVert,
  Schedule,
  CheckCircle,
  Warning,
  Error,
  Refresh,
  FilterList,
  ExpandMore,
  Reply,
  Forward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const ContractorMessagingPage = () => {
  const navigate = useNavigate();

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Mock conversation data for contractor
  const mockConversations = [
    {
      id: "CONV001",
      subject: "PKG2024001 - Foundation Inspection Questions",
      permitNumber: "PKG2024001",
      address: "123 Oak Street, Jacksonville, FL 32207",
      participants: ["Contractor", "John Smith (Inspector)", "PP Admin"],
      status: "active",
      priority: "high",
      lastMessage: {
        id: "MSG003",
        sender: "John Smith (Inspector)",
        timestamp: "2024-02-16T14:30:00Z",
        content:
          "The foundation looks good overall. Just need clarification on the rebar spacing in section A-3.",
        attachments: [],
      },
      unreadCount: 1,
      messages: [
        {
          id: "MSG001",
          sender: "Contractor",
          timestamp: "2024-02-15T10:00:00Z",
          content:
            "Hi, I have some questions about the foundation inspection scheduled for tomorrow. Can you please review the photos I uploaded?",
          attachments: [
            { id: "ATT001", name: "foundation_photos.jpg", type: "image" },
          ],
        },
        {
          id: "MSG002",
          sender: "PP Admin",
          timestamp: "2024-02-15T15:30:00Z",
          content:
            "I've forwarded your message to Inspector John Smith. He'll review the photos and get back to you.",
          attachments: [],
        },
        {
          id: "MSG003",
          sender: "John Smith (Inspector)",
          timestamp: "2024-02-16T14:30:00Z",
          content:
            "The foundation looks good overall. Just need clarification on the rebar spacing in section A-3. Can you provide a close-up photo of that area?",
          attachments: [],
        },
      ],
      type: "inspection",
      tags: ["foundation", "inspection", "photos"],
    },
    {
      id: "CONV002",
      subject: "PKG2024003 - Plan Review Corrections",
      permitNumber: "PKG2024003",
      address: "789 Maple Drive, Green Cove Springs, FL 32043",
      participants: ["Contractor", "PP Reviewer"],
      status: "correction_needed",
      priority: "medium",
      lastMessage: {
        id: "MSG006",
        sender: "PP Reviewer",
        timestamp: "2024-02-14T16:45:00Z",
        content:
          "Please revise the structural plans per the attached comments. The beam calculations need to be updated according to IBC 1604.3.",
        attachments: [
          { id: "ATT003", name: "review_comments.pdf", type: "pdf" },
        ],
      },
      unreadCount: 1,
      messages: [
        {
          id: "MSG004",
          sender: "Contractor",
          timestamp: "2024-02-10T09:15:00Z",
          content:
            "Submitted revised plans for review. Please let me know if any changes are needed.",
          attachments: [
            { id: "ATT002", name: "revised_structural_plans.pdf", type: "pdf" },
          ],
        },
        {
          id: "MSG005",
          sender: "PP Reviewer",
          timestamp: "2024-02-12T11:30:00Z",
          content:
            "Thank you for the submission. I'm reviewing the plans now and will get back to you within 2 business days.",
          attachments: [],
        },
        {
          id: "MSG006",
          sender: "PP Reviewer",
          timestamp: "2024-02-14T16:45:00Z",
          content:
            "Please revise the structural plans per the attached comments. The beam calculations need to be updated according to IBC 1604.3.",
          attachments: [
            { id: "ATT003", name: "review_comments.pdf", type: "pdf" },
          ],
        },
      ],
      type: "submission",
      tags: ["plan review", "structural", "corrections"],
    },
    {
      id: "CONV003",
      subject: "PKG2024002 - General Questions",
      permitNumber: "PKG2024002",
      address: "456 Pine Avenue, St Augustine, FL 32084",
      participants: ["Contractor", "PP Admin"],
      status: "completed",
      priority: "low",
      lastMessage: {
        id: "MSG008",
        sender: "PP Admin",
        timestamp: "2024-02-10T10:00:00Z",
        content:
          "Perfect! Your permit application has been processed and submitted to the city. You should receive confirmation within 3-5 business days.",
        attachments: [],
      },
      unreadCount: 0,
      messages: [
        {
          id: "MSG007",
          sender: "Contractor",
          timestamp: "2024-02-08T14:20:00Z",
          content:
            "What's the status of my permit application? It's been a week since I submitted all the documents.",
          attachments: [],
        },
        {
          id: "MSG008",
          sender: "PP Admin",
          timestamp: "2024-02-10T10:00:00Z",
          content:
            "Perfect! Your permit application has been processed and submitted to the city. You should receive confirmation within 3-5 business days.",
          attachments: [],
        },
      ],
      type: "general",
      tags: ["permit status", "application"],
    },
  ];

  const [conversations, setConversations] = useState(mockConversations);

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { color: "primary", label: "Active" },
      correction_needed: { color: "warning", label: "Action Required" },
      completed: { color: "success", label: "Completed" },
      pending: { color: "info", label: "Pending" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high":
        return <Error color="error" fontSize="small" />;
      case "medium":
        return <Warning color="warning" fontSize="small" />;
      case "low":
        return <CheckCircle color="success" fontSize="small" />;
      default:
        return <Circle color="disabled" fontSize="small" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "inspection":
        return <Build color="primary" />;
      case "submission":
        return <Assignment color="secondary" />;
      case "general":
        return <Chat color="info" />;
      default:
        return <Chat />;
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    const matchesSearch =
      conv.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.permitNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.address.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || conv.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleSendMessage = () => {
    if (!selectedConversation || !newMessage.trim()) return;

    const newMsg = {
      id: `MSG_${Date.now()}`,
      sender: "Contractor",
      timestamp: new Date().toISOString(),
      content: newMessage.trim(),
      attachments: [],
    };

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === selectedConversation.id
          ? {
              ...conv,
              messages: [...conv.messages, newMsg],
              lastMessage: newMsg,
            }
          : conv
      )
    );

    setNewMessage("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <Box sx={{ p: 3, height: "calc(100vh - 200px)" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Messages
      </Typography>

      <Grid container spacing={3} sx={{ height: "100%" }}>
        {/* Conversations List */}
        <Grid xs={12} md={4} sx={{ height: "100%" }}>
          <Paper
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Search and Filter */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                      >
                        <FilterList />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => setFilterAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setStatusFilter("all");
                    setFilterAnchorEl(null);
                  }}
                >
                  All Conversations
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStatusFilter("active");
                    setFilterAnchorEl(null);
                  }}
                >
                  Active
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStatusFilter("correction_needed");
                    setFilterAnchorEl(null);
                  }}
                >
                  Action Required
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStatusFilter("completed");
                    setFilterAnchorEl(null);
                  }}
                >
                  Completed
                </MenuItem>
              </Menu>
            </Box>

            {/* Conversations List */}
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <List>
                {filteredConversations.map((conversation) => (
                  <ListItem
                    key={conversation.id}
                    button
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      "&.Mui-selected": {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Badge
                        badgeContent={conversation.unreadCount}
                        color="error"
                        invisible={conversation.unreadCount === 0}
                      >
                        {getTypeIcon(conversation.type)}
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight:
                                conversation.unreadCount > 0
                                  ? "bold"
                                  : "normal",
                              flexGrow: 1,
                            }}
                          >
                            {conversation.permitNumber}
                          </Typography>
                          {getPriorityIcon(conversation.priority)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {conversation.subject}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {getStatusChip(conversation.status)}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTimestamp(
                                conversation.lastMessage.timestamp
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Message Thread */}
        <Grid xs={12} md={8} sx={{ height: "100%" }}>
          <Paper
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "grey.50",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {selectedConversation.subject}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {selectedConversation.address}
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {getStatusChip(selectedConversation.status)}
                        <Chip
                          label={selectedConversation.type}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Assignment />}
                      onClick={() =>
                        navigate(
                          `/contractor/permits/${selectedConversation.permitNumber.replace("PKG", "")}/inspections`
                        )
                      }
                    >
                      View Permit
                    </Button>
                  </Box>

                  {/* Participants */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Participants: {selectedConversation.participants.join(", ")}
                  </Typography>
                </Box>

                {/* Messages */}
                <Box
                  sx={{
                    flexGrow: 1,
                    overflow: "auto",
                    p: 2,
                    backgroundColor: "grey.25",
                  }}
                >
                  {selectedConversation.messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: "flex",
                        mb: 2,
                        flexDirection:
                          message.sender === "Contractor"
                            ? "row-reverse"
                            : "row",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          mx: 1,
                          backgroundColor:
                            message.sender === "Contractor"
                              ? "primary.main"
                              : "secondary.main",
                        }}
                      >
                        {message.sender.charAt(0)}
                      </Avatar>
                      <Paper
                        sx={{
                          p: 2,
                          maxWidth: "70%",
                          backgroundColor:
                            message.sender === "Contractor"
                              ? "primary.light"
                              : "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            {message.sender}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(message.timestamp)}
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {message.content}
                        </Typography>

                        {/* Attachments */}
                        {message.attachments.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {message.attachments.map((attachment) => (
                              <Chip
                                key={attachment.id}
                                icon={<AttachFile />}
                                label={attachment.name}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  // Handle attachment download
                                  // );
                                }}
                                sx={{ mr: 1 }}
                              />
                            ))}
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </Box>

                {/* Message Input */}
                <Box
                  sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      variant="outlined"
                      size="small"
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      startIcon={<Send />}
                    >
                      Send
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                }}
              >
                <Box>
                  <Chat sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a conversation to start messaging
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a conversation from the list to view and send
                    messages
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractorMessagingPage;
